import { events } from "@constants/tracking";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import type {
	BaseItemSchema,
	OriginalItemTypes,
	RenderableItemTypes,
} from "@memorang/types/src/session/new-session";
import { useToast } from "@memorang/ui/src/components/Toast";
import { trackCustomEvent } from "analytics";
import { useLocalSearchParams } from "expo-router";
import { useEffect, useState } from "react";
import BaseItemType from "../components/itemtypes/question/BaseItemType";
import { isMultiSelect } from "../helpers/session";
import useAIFeedbackStore from "../hooks/useAIFeedbackStore";
import useSessionStore from "../hooks/useSessionStore";
import { handlePostAnswer } from "./StudySessionBottombarContainer/helpers";

interface Props {
	item: BaseItemSchema;
	originalItemType: OriginalItemTypes;
	mappedItemType: RenderableItemTypes;
	toggleShowReadMore?: (options?: { scrollToHighlight?: boolean }) => void;
}

const BaseItemTypeContainer = ({
	item,
	originalItemType,
	mappedItemType,
	toggleShowReadMore,
}: Props) => {
	const { showToast } = useToast();

	const {
		currentExamContext: { projectId, organizationId, examId },
	} = useExamContext();

	const updateItemStates = useSessionStore((state) => state.updateItemStates);
	const itemStates = useSessionStore((state) => state.itemStates);
	const isSummativeTest = useSessionStore((state) => state.isSummativeTest);
	const inReviewMode = useSessionStore((state) => state.inReviewMode);
	const updateFormStates = useSessionStore((state) => state.updateFormStates);
	const answers = useSessionStore((state) => state.answers);
	const [startTime, setStartTime] = useState(new Date());
	const { index } = useLocalSearchParams<{
		index: string;
	}>();
	const sessionId = useSessionStore((state) => state.id);

	const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;

	const itemId = item.id!;

	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	useEffect(() => {
		if (isSummativeTest) {
			setStartTime(new Date());
		}
	}, [itemId, isSummativeTest]);
	const userResponse = useAIFeedbackStore((state) => state.userResponse);
	const currentUserResponse = userResponse?.[itemId];

	const currentAnswer = answers?.get(itemId);
	const currentAnswerFormStates = currentAnswer?.answeredFormStates;
	const itemState = itemStates?.get(itemId);
	const isAnswered = isSummativeTest
		? inReviewMode && item.answer != null
		: itemStates?.get(itemId)?.answered || item.answer != null;

	const selectedChoiceIds =
		itemState?.selectedChoiceIds || item.answer?.selectedChoiceIds;
	const correctAnswerIds =
		itemState?.correctAnswerIds ||
		item.content?.choices
			?.filter((choice) => choice.isCorrect)
			.map((choice) => choice.id);

	const crossedOutChoiceIds = itemState?.crossedOutChoiceIds;

	const correctAnswerOrders = item.response?.correctResponse?.map(
		(choice) => choice.id,
	);

	const shouldPreventSelection = (choiceId: string) => {
		const maxChoiceCount =
			item.response?.numChoicesRequired?.max || item.content?.maxChoiceCount;
		const multiSelect = isMultiSelect({
			maxChoiceCount,
			minChoiceCount: item.content?.minChoiceCount,
			mappedItemType,
		});
		return (
			multiSelect &&
			maxChoiceCount &&
			selectedChoiceIds &&
			selectedChoiceIds.length >= maxChoiceCount &&
			!selectedChoiceIds.includes(choiceId)
		);
	};

	const handleSelectChoice = (
		choiceId: string,
		crossingOut?: boolean,
		currentOrders?: string[],
	) => {
		const maxChoiceCount =
			item.response?.numChoicesRequired?.max || item.content?.maxChoiceCount;
		if (shouldPreventSelection(choiceId)) {
			showToast({
				message: `Maximum limit reached. You can only select up to ${maxChoiceCount} answers.`,
				duration: 500,
			});
			return;
		}

		trackCustomEvent({
			eventName: crossingOut
				? events.sessionItemCrossedOut
				: events.sessionItemOptionSelected,
			choiceId,
			itemId: itemId,
			sessionId: sessionId,
			isSummativeTest: isSummativeTest ? "true" : "false",
		});

		updateItemStates({
			itemId,
			selectedChoiceId: choiceId,
			multiSelect: isMultiSelect({
				maxChoiceCount,
				minChoiceCount: item.content?.minChoiceCount,
				mappedItemType,
			}),
			crossOut: crossingOut,
			currentOrders,
		});
		if (isSummativeTest) {
			handlePostAnswer({
				state: useSessionStore.getState(),
				startTime,
				projectId,
				organizationId,
				examId,
				submitAnswer: true,
				mappedItemType,
				currentItemIndex,
			});
		}
	};

	const handleHighlightedTextPress = () => {
		toggleShowReadMore?.({ scrollToHighlight: true });
	};

	const handleUpdateFormStates = (formStates: Record<string, string>) => {
		updateFormStates(itemId, formStates);
	};

	return (
		<BaseItemType
			item={item}
			originalItemType={originalItemType}
			mappedItemType={mappedItemType}
			selectedChoiceIds={selectedChoiceIds}
			correctAnswerIds={correctAnswerIds}
			crossedOutChoiceIds={crossedOutChoiceIds}
			isAnswered={isAnswered}
			currentUserResponse={currentUserResponse}
			isSummativeTest={isSummativeTest}
			onSelectChoice={handleSelectChoice}
			toggleShowReadMore={toggleShowReadMore}
			correctAnswerOrders={correctAnswerOrders}
			handleHighlightedTextPress={handleHighlightedTextPress}
			updateFormStates={handleUpdateFormStates}
			currentAnswerFormStates={currentAnswerFormStates}
		/>
	);
};

export default BaseItemTypeContainer;
